import { useMemo } from "react";
import type { RequestStatus } from "@gw/shared-types";
import type { RequestAggregateGroupBy, RequestAggregateRowDto } from "@/api/endpoints";
import { SortHead, type SortState } from "./SortHead";
import { Sparkline } from "@/features/taskEfficiency/components/Sparkline";
import { REQUEST_GROUP_LABEL_BY_KEY } from "./RequestGroupByChips";
import { statusLabel } from "@/lib/labels";

// Per-column display formatters. Raw values from the aggregator come straight from the database
// (e.g. CalculatedStatus = "InProgress"); humanize them for the cell text without changing the
// underlying values that drive grouping / filtering / drill-in.
const VALUE_FORMATTERS: Partial<Record<RequestAggregateGroupBy, (v: string) => string>> = {
  calculatedStatus: (v) => statusLabel(v as RequestStatus),
};

function formatGroupValue(key: RequestAggregateGroupBy, raw: string | undefined): string {
  if (raw == null || raw === "") return "—";
  return VALUE_FORMATTERS[key]?.(raw) ?? raw;
}

// Column tooltips for the per-group task-timing medians. Wording mirrors the request-detail Tasks
// table (Idle + Wait + Execution = Duration); each value is the median across the group's completed
// requests, in the unit selected by the bd/cd toggle.
const TIP_TASK_IDLE =
  "Median across the group's completed requests of each request's total task Idle — elapsed time " +
  "before a task opened with no task assigned (gaps between tasks, re-issues, weekends/holidays). " +
  "Idle + Wait + Execution = Duration.";
const TIP_TASK_WAIT =
  "Median across the group's completed requests of each request's total task Wait — time from a " +
  "task being assigned to work starting (the hand-off delay).";
const TIP_TASK_EXEC =
  "Median across the group's completed requests of each request's total task Execution — hands-on " +
  "time from a task starting to completing (the task's duration).";
const TIP_TASK_DURATION =
  "Median across the group's completed requests of each request's total task Duration = Idle + " +
  "Wait + Execution. Shown in the unit selected by the bd/cd toggle.";
const TIP_PRE_ONBOARDING =
  "Median across the group's completed requests of each request's Pre-Verification duration — time " +
  "from the linked pre-verification request's start to when the work order was created. Only counts " +
  "onboardings that originated from a pre-verification request (others are excluded).";

interface Props {
  rows: RequestAggregateRowDto[];
  groupBy: RequestAggregateGroupBy[];
  sort: SortState;
  onSortChange: (s: SortState) => void;
  onRowClick?: (row: RequestAggregateRowDto) => void;
  unit: "bd" | "cd";
}

// The active unit's stats for one row — the server sends both bd and cd.
function pick(row: RequestAggregateRowDto, unit: "bd" | "cd") {
  return unit === "cd"
    ? {
        median: row.medianCd,
        mean: row.meanCd,
        p75: row.p75Cd,
        p95: row.p95Cd,
        medTaskIdle: row.medianTaskIdleCd,
        medTaskWait: row.medianTaskWaitCd,
        medTaskExec: row.medianTaskExecCd,
        medTaskDuration: row.medianTaskDurationCd,
        medPreOnboarding: row.medianPreOnboardingCd,
        slowRate: row.slowRateCd,
        distribution: row.distributionCd,
      }
    : {
        median: row.medianBd,
        mean: row.meanBd,
        p75: row.p75Bd,
        p95: row.p95Bd,
        medTaskIdle: row.medianTaskIdleBd,
        medTaskWait: row.medianTaskWaitBd,
        medTaskExec: row.medianTaskExecBd,
        medTaskDuration: row.medianTaskDurationBd,
        medPreOnboarding: row.medianPreOnboardingBd,
        slowRate: row.slowRate,
        distribution: row.distribution,
      };
}

// Returns null for "no value" (e.g. a group with no completed/task data) so the sort can push those
// rows last in BOTH directions — using a sentinel like -1 would flip them to the top on ascending.
function pluck(row: RequestAggregateRowDto, key: string, unit: "bd" | "cd"): number | null {
  const m = pick(row, unit);
  switch (key) {
    case "n":
      return row.sampleSize;
    case "completed":
      return row.completed;
    case "inProgress":
      return row.inProgress;
    case "median":
      return m.median;
    case "average":
      return m.mean;
    case "p75":
      return m.p75;
    case "p95":
      return m.p95;
    case "medTaskIdle":
      return m.medTaskIdle;
    case "medTaskWait":
      return m.medTaskWait;
    case "medTaskExec":
      return m.medTaskExec;
    case "medTaskDuration":
      return m.medTaskDuration;
    case "medPreOnboarding":
      return m.medPreOnboarding;
    case "slowRate":
      return m.slowRate;
    case "reworkRate":
      return row.reworkRate;
    default:
      return 0;
  }
}

export function RequestAggregateTable({
  rows,
  groupBy,
  sort,
  onSortChange,
  onRowClick,
  unit,
}: Props) {
  const sorted = useMemo(() => {
    const mult = sort.dir === "asc" ? 1 : -1;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = pluck(a, sort.key, unit);
      const bv = pluck(b, sort.key, unit);
      // Missing values always sort last, regardless of direction — a "—" row should never jump to
      // the top just because the sort is ascending.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * mult;
      if (av > bv) return 1 * mult;
      return 0;
    });
    return copy;
  }, [rows, sort, unit]);

  // Global P95 max for sparkline visual comparability across groups (in the active unit).
  const globalMaxP95 = useMemo(
    () => Math.max(1, ...rows.map((r) => (unit === "cd" ? r.p95Cd : r.p95Bd) ?? 0)),
    [rows, unit],
  );

  if (rows.length === 0) {
    return (
      <div className="empty">
        <h4>No groups match the filters</h4>
        <p>Try expanding the date range or clearing filters.</p>
      </div>
    );
  }

  return (
    <div className="tbl-wrap" style={{ maxHeight: "calc(100vh - 380px)" }}>
      <table className="tbl">
        <thead>
          <tr>
            {groupBy.map((k, i) => (
              <th key={k} className={i === 0 ? "col-pin col-pin-group col-pin-last" : undefined}>
                {REQUEST_GROUP_LABEL_BY_KEY[k]}
              </th>
            ))}
            <SortHead label="n" sortKey="n" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="Completed" sortKey="completed" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="In progress" sortKey="inProgress" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="Median" sortKey="median" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="Average" sortKey="average" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="P75" sortKey="p75" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="P95" sortKey="p95" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="Med. pre-verification" sortKey="medPreOnboarding" sort={sort} setSort={onSortChange} numeric info={TIP_PRE_ONBOARDING} />
            <SortHead label="Med. task idle" sortKey="medTaskIdle" sort={sort} setSort={onSortChange} numeric info={TIP_TASK_IDLE} />
            <SortHead label="Med. task wait" sortKey="medTaskWait" sort={sort} setSort={onSortChange} numeric info={TIP_TASK_WAIT} />
            <SortHead label="Med. task exec." sortKey="medTaskExec" sort={sort} setSort={onSortChange} numeric info={TIP_TASK_EXEC} />
            <SortHead label="Med. task dur." sortKey="medTaskDuration" sort={sort} setSort={onSortChange} numeric info={TIP_TASK_DURATION} />
            <SortHead label="% slow" sortKey="slowRate" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="% rework" sortKey="reworkRate" sort={sort} setSort={onSortChange} numeric />
            <th>Distribution</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            // Key on the row's actual group-values shape (whatever the server returned), not the
            // current groupBy prop. If those diverge — e.g. a stale placeholder row is rendered
            // against a new groupBy — a key derived from the prop projection would collapse
            // distinct rows to the same composite (trailing-empty for missing dimensions). The
            // server never emits two groups with identical groupValues, so JSON.stringify is
            // unique by construction.
            const rowKey = JSON.stringify(row.groupValues);
            const m = pick(row, unit);
            const p95Severe =
              m.p95 != null && m.median != null && m.median > 0 && m.p95 > m.median * 4;
            return (
              <tr key={rowKey} onClick={() => onRowClick?.(row)}>
                {groupBy.map((k, i) => (
                  <td key={k} className={i === 0 ? "col-pin col-pin-group col-pin-last" : undefined}>
                    <span className="cell-primary">{formatGroupValue(k, row.groupValues[k])}</span>
                  </td>
                ))}
                <td className="numeric">{row.sampleSize}</td>
                <td className="numeric">
                  <span className="cell-secondary">{row.completed}</span>
                </td>
                <td className="numeric">
                  <span className="cell-secondary">{row.inProgress}</span>
                </td>
                <td className="numeric">
                  {m.median != null ? (
                    <strong
                      style={{
                        color:
                          m.median > 6
                            ? "var(--gw-error-600)"
                            : m.median > 3
                              ? "var(--gw-warning-700)"
                              : "inherit",
                      }}
                    >
                      {m.median.toFixed(2)}
                    </strong>
                  ) : (
                    <span style={{ color: "var(--fg-3)" }}>—</span>
                  )}
                </td>
                <td className="numeric">{m.mean != null ? m.mean.toFixed(2) : "—"}</td>
                <td className="numeric">{m.p75 != null ? m.p75.toFixed(2) : "—"}</td>
                <td
                  className="numeric"
                  style={{
                    color: p95Severe ? "var(--gw-error-600)" : "inherit",
                    fontWeight: p95Severe ? 600 : 400,
                  }}
                >
                  {m.p95 != null ? m.p95.toFixed(2) : "—"}
                </td>
                <td className="numeric">{m.medPreOnboarding != null ? m.medPreOnboarding.toFixed(2) : "—"}</td>
                <td className="numeric">{m.medTaskIdle != null ? m.medTaskIdle.toFixed(2) : "—"}</td>
                <td className="numeric">{m.medTaskWait != null ? m.medTaskWait.toFixed(2) : "—"}</td>
                <td className="numeric">{m.medTaskExec != null ? m.medTaskExec.toFixed(2) : "—"}</td>
                <td className="numeric">{m.medTaskDuration != null ? m.medTaskDuration.toFixed(2) : "—"}</td>
                <td
                  className="numeric"
                  style={{
                    color:
                      m.slowRate > 0.3
                        ? "var(--gw-error-600)"
                        : m.slowRate > 0.15
                          ? "var(--gw-warning-700)"
                          : "inherit",
                  }}
                >
                  {(m.slowRate * 100).toFixed(0)}%
                </td>
                <td
                  className="numeric"
                  style={{
                    color: row.reworkRate > 0.2 ? "var(--gw-warning-700)" : "inherit",
                  }}
                >
                  {(row.reworkRate * 100).toFixed(0)}%
                </td>
                <td style={{ width: 130 }}>
                  <Sparkline values={m.distribution} max={globalMaxP95} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
